import "../sellscreendialogs.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useState,
} from "react";
import { useShopfrontDispatch, useShopfrontSelector } from "../../../../_Bootstrap/hooks";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { addPaymentToSale, type SaleAddPaymentOptions } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import Button from "../../../../Common/Buttons/Button";
import NoTranslate from "../../../../Common/NoTranslate";
import PopupManager from "../../../../Common/Popups/PopupManager";
import TouchNumbers from "../../../../Common/TouchNumbers/TouchNumbers";
import { getEnterpriseAuthToken } from "../../../../Data/_Bases/BaseEnterpriseRepository";
import { currency } from "../../../../Utilities/Generic/Numbers";
import { formatDate } from "../../../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getPolicy } from "../../../../Utilities/Platform/Policies/Policies";
import { getMainHost, getVendorSubdomain } from "../../../../Utilities/Platform/Request";

export const RequestPayGiftCard: FC<{
    amount: number;
    method: string;
    options: SaleAddPaymentOptions;
}> = ({
    amount: saleAmount,
    method,
    options,
}) => {
    const saleTotal = useShopfrontSelector(state => state.sellscreen.sale.totals.sale);
    const saleRemaining = useShopfrontSelector(state => state.sellscreen.sale.totals.remaining);

    const [ value, setValue ] = useState(saleAmount.toString());
    const [ paying, setPaying ] = useState(false);
    const dispatch = useShopfrontDispatch();

    const addCard = useCallback(async () => {
        if(!options.giftcard || options.giftcard === "auto") {
            return;
        }

        const amount = parseFloat(value);

        if(isNaN(amount)) {
            PopupManager.error("The specified amount is invalid");

            return;
        }

        if(Math.sign(amount) !== Math.sign(amount)) {
            PopupManager.error(
                "Invalid amount specified, both the gift card payment and the sale total must be on the same " +
                "side of zero"
            );

            return;
        }

        const currentAmount = options.giftcard.current_amount;

        if(Math.abs(amount) > Math.abs(currentAmount)) {
            PopupManager.error("Invalid amount specified, the gift card does not have enough funds on it");

            return;
        }

        if(amount === 0) {
            PopupManager.error("You must specify an amount");

            return;
        }

        if(amount > saleAmount) {
            PopupManager.error("You cannot overpay with a gift card");

            return;
        }

        if(options.source !== "internal") {
            setPaying(true);
            let source;
            const sources = getPolicy("gift_card_sources", []);

            for(let i = 0, l = sources.length; i < l; i++) {
                if(sources[i].connection === options.source) {
                    source = sources[i].address;
                    break;
                }
            }

            if(!source) {
                PopupManager.error("Gift card source is no longer available, please try again");

                return;
            }

            const response = await fetch(`${source}/${options.giftcard.id}`, {
                method : "PUT",
                headers: {
                    "x-shopfront-token" : await getEnterpriseAuthToken(options.source),
                    "x-shopfront-vendor": getVendorSubdomain(),
                    "x-shopfront-host"  : getMainHost(),
                    "accept"            : "application/json",
                },
                body: JSON.stringify({
                    amount,
                }),
            });

            const body = await response.json();

            if(response.status !== 200) {
                console.error("Unable to redeem a gift card", body);
                let message;

                if(body.error && body.message) {
                    message = body.message;
                } else {
                    message = "Unknown Error";
                }

                PopupManager.error(`Unable to redeem gift card, response from Enterprise: ${message}`);

                setPaying(false);

                return;
            }
        }

        dispatch(hideDialog());
        dispatch(addPaymentToSale(method, amount, {
            ...options,
            exact: false,
        }));
    }, [ dispatch, method, options, saleAmount, value ]);

    useEffect(() => {
        if(!options.giftcard || options.giftcard === "auto") {
            PopupManager.error("The gift card has not been added to the sale correctly");
            dispatch(hideDialog());

            return;
        }

        setValue(Math.min(saleAmount, options.giftcard.current_amount).toString());
    }, [ saleAmount, options.giftcard, dispatch ]);

    if(!options.giftcard || options.giftcard === "auto") {
        return null;
    }

    const currentAmount = options.giftcard.current_amount;
    const giftCardAmount = parseFloat(value);

    if(paying) {
        return (
            <div>
                <p>Processing payment...</p>
                <div>
                    <FontAwesomeLight icon="spinner-third" spin />
                </div>
            </div>
        );
    }

    return (
        <div className="request-pay-gift-card">
            <div className="gift-card">
                <div className="gift-card-details">
                    <table>
                        <tbody>
                            <tr>
                                <th>Code</th>
                                <td>{options.giftcard.code}</td>
                            </tr>
                            <tr>
                                <th>Expiry</th>
                                <td>{formatDate(options.giftcard.expiry)}</td>
                            </tr>
                            <tr>
                                <td />
                                <td />
                            </tr>
                            <tr>
                                <th>Gift Card Balance</th>
                                <td><NoTranslate>{currency(currentAmount)}</NoTranslate></td>
                            </tr>
                            <tr>
                                <th>After Payment Balance</th>
                                <td>
                                    <NoTranslate>{currency(currentAmount - giftCardAmount)}</NoTranslate>
                                </td>
                            </tr>
                            <tr>
                                <td />
                                <td />
                            </tr>
                            <tr>
                                <td />
                                <td />
                            </tr>
                            <tr>
                                <th>Sale Total</th>
                                <td><NoTranslate>{currency(saleTotal)}</NoTranslate></td>
                            </tr>
                            <tr>
                                <th>Current Sale Balance</th>
                                <td><NoTranslate>{currency(saleRemaining)}</NoTranslate></td>
                            </tr>
                            <tr>
                                <th>Sale Balance After Payment</th>
                                <td><NoTranslate>{currency(saleRemaining - giftCardAmount)}</NoTranslate>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="gift-card-payment">
                    <label>
                        Amount to pay
                        <TouchNumbers
                            static
                            hideConfirm
                            input="currency"
                            value={value}
                            onChange={val => setValue(val)}
                            onConfirm={addCard}
                            autoFocus
                        />
                    </label>
                </div>
            </div>
            <div className="buttons">
                <Button
                    type="button"
                    classes={[ "button-cancel" ]}
                    onClick={() => dispatch(hideDialog())}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "button-save" ]}
                    onClick={addCard}
                >
                    Pay
                </Button>
            </div>
        </div>
    );
};
